import React, { useMemo } from "react";
import { getLocalizedString } from "@icarius-localization/strings";
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
} from "@material-ui/core";
import CommonPage from "@icarius-common/commonPage";
import FabButton from "@icarius-common/fabButton";
import SendMailDialog from "@icarius-common/mailDialog/sendMailDialog";
import Masonry from "react-masonry-css";
import BotAssistant from "@icarius-common/botAssistant/components";
import NoGroups from "../noGroups";
import FormDialog from "./dialogs/formDialog";
import MembersDialog from "./dialogs/membersDialog";
import useMyGroups from "./useMyGroups";
import Group from "./group";
import useBotAssistant from "@icarius-common/botAssistant/components/useBotAssistant";

const BREAKPOINTS = {
  default: 3,
  1920: 4,
  1440: 3,
  1280: 3,
  960: 2,
  750: 2,
  600: 1,
  480: 1
}

const MyGroups = () => {

  const {
    conversationId,
    conversationIsAssistantLoading,
    selectedConversationMessages,
    handleOpenBotAssistant,
    handleCloseBotAssistant,
    handleSubmitMessage,
    handleCancelSubmitMessage,
    handleResetConversation,
  } = useBotAssistant();

  const {
    isLoading,
    isSendingMail,
    groups,
    employees,
    tags,
    botAssistantEnabled,
    state,
    handlers,
  } = useMyGroups(conversationId, handleCloseBotAssistant);

  const filteredGroups = useMemo(() => {
    if (!state.tagFilter) return groups;
    return groups.filter((item) => item.tags?.includes(state.tagFilter));
  }, [groups, state.tagFilter])

  return (
    <CommonPage
      title={getLocalizedString("myGroupsTitle")}
      isLoading={isLoading || isSendingMail}
      isNotGridPage
    >
      <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 10, margin: "0px 10px 10px" }}>
        <BotAssistant
          key={conversationId}
          open={Boolean(conversationId)}
          isLoading={conversationIsAssistantLoading}
          subtitle={groups.find((group) => group.id === conversationId)?.name}
          messages={selectedConversationMessages}
          handleSubmit={handleSubmitMessage}
          handleCancelSubmit={handleCancelSubmitMessage}
          handleResetConversation={handleResetConversation}
          handleClose={handleCloseBotAssistant}
        />
        <FabButton
          title={getLocalizedString("newGroup")}
          onClick={() => handlers.handleOpenGroupDialog()}
        />
        {
          Boolean(tags?.length) &&
          <div style={{ width: 300, minWidth: 300, paddingLeft: 10 }}>
            <FormControl fullWidth>
              <InputLabel shrink>{getLocalizedString("tagTitle")}</InputLabel>
              <Select
                value={state.tagFilter}
                onChange={(e) => handlers.handleFilterByTag(e.target.value)}
                margin={"none"}
                displayEmpty
              >
                <MenuItem className={"whiteText"} value={""}>
                  {getLocalizedString("everyone")}
                </MenuItem>
                {
                  tags?.map((item) => (
                    <MenuItem
                      className={"whiteText"}
                      key={item.key}
                      value={item.key}
                    >
                      {item.value}
                    </MenuItem>
                  ))
                }
              </Select>
            </FormControl>
          </div>
        }
      </div>
      {
        !Boolean(groups?.length) && !isLoading &&
        <NoGroups
          onClick={() => handlers.handleOpenGroupDialog()}
        />
      }
      {
        Boolean(filteredGroups?.length) &&
        <div className="flex flex-wrap w-full">
          <Masonry
            breakpointCols={BREAKPOINTS}
            className="my-masonry-grid flex w-full"
            columnClassName="my-masonry-grid_column flex flex-col p-4 py-0"
          >
            {
              filteredGroups.map((group) => {
                return (
                  <div
                    key={group.id}
                    className={"mb-8"}
                  >
                    <Group
                      tags={tags}
                      data={group}
                      botAssistantEnabled={botAssistantEnabled}
                      handleOpenEditGroupDialog={handlers.handleOpenGroupDialog}
                      handleOpenDeleteGroupDialog={handlers.handleOpenDeleteGroupDialog}
                      handleOpenLeaveGroupDialog={handlers.handleOpenLeaveDialog}
                      handleOpenMembersDialog={handlers.handleOpenMembersDialog}
                      handleOpenMailDialog={handlers.handleOpenMailDialog}
                      handleOpenGroupChat={() => handleOpenBotAssistant(group.id)}
                    />
                  </div>
                )
              })
            }
          </Masonry>
        </div>
      }
      {
        state.formDialogIsOpen &&
        <FormDialog
          open={state.formDialogIsOpen}
          data={state.selectedGroup}
          employees={employees}
          tags={tags}
          botAssistantEnabled={botAssistantEnabled}
          handleClose={handlers.handleCloseGroupDialog}
        />
      }
      {
        state.membersDialogIsOpen &&
        <MembersDialog
          open={state.membersDialogIsOpen}
          participants={state.selectedGroup.participants}
          employees={employees}
          handleClose={handlers.handleCloseMembersDialog}
        />
      }
      {
        state.mailDialogIsOpen && !isLoading && !isSendingMail &&
        <SendMailDialog
          open={state.mailDialogIsOpen}
          employees={state.mailUsers}
          groupName={state.groupName}
          handleClose={handlers.handleCloseMailDialog}
          origin={"Groups"}
        />
      }
    </CommonPage>
  )
}

export default MyGroups;